/**
 * Runtime preview-port discovery (Claudespace v3).
 *
 * Polls the pod's listening TCP sockets and reports the delta vs a baseline
 * scan taken at startup — i.e. before the start command has had a chance to
 * bind anything — so sshd (2222), the agent's own internals, and sidecar
 * services are never reported. The confirmed set is pushed to the API via
 * `reportDiscoveredPorts` only when it CHANGES (full-replacement semantics),
 * where it lands on `WorkspaceSession.discoveredPorts` and feeds the
 * workspace-attach preview-port list.
 *
 * Bind addresses matter: the preview-router proxies to `http://{podIp}:{port}`,
 * never to loopback, so a listener bound only to 127.0.0.1/::1 is unreachable
 * from outside the pod by construction. Loopback-only listeners are therefore
 * excluded from the report (they'd render a dead eyeball entry that 502s) and
 * logged once so the "bind 0.0.0.0" fix is discoverable from the agent log.
 *
 * Source of truth is /proc/net/tcp + /proc/net/tcp6 — present on every Linux
 * pod, needs no binaries (the base image has no `ss`/`netstat` from iproute2).
 * A `netstat` fallback exists for darwin so the real-listener test runs on
 * developer machines; production pods are always Linux.
 */
import { readFile } from "node:fs/promises";
import { execFile } from "node:child_process";
import type { WorkspaceDiscoveredPort } from "@project/shared";

// ── Scanning ────────────────────────────────────────────────────────────────

/** TCP state 0A = LISTEN in /proc/net/tcp[6]. */
const PROC_TCP_LISTEN_STATE = "0A";

/** One LISTEN socket: its local port and whether the bind is loopback-only. */
export interface ListeningSocket {
  port: number;
  loopback: boolean;
}

/**
 * Scan result: every LISTEN port, plus the subset whose listeners are ALL
 * bound to loopback (unreachable via the pod IP). A port with both a loopback
 * and a wildcard/external listener is NOT loopback-only.
 */
export interface ListeningPortScan {
  ports: Set<number>;
  loopbackOnly: Set<number>;
}

/**
 * Is a /proc/net/tcp[6] hex local address a loopback bind?
 * - tcp (8 hex chars): a little-endian 32-bit word — the LAST byte pair is the
 *   first octet, so 127.0.0.0/8 ⇒ trailing "7F" (e.g. 127.0.0.1 = "0100007F").
 * - tcp6 (32 hex chars): four little-endian 32-bit words. `::1` is all-zero
 *   with a final word of "01000000"; a v4-mapped `::ffff:127.x.x.x` has word 3
 *   "FFFF0000" and a 127/8 v4 word. The wildcards (0.0.0.0 / ::) are all-zero
 *   and NOT loopback. Unrecognized shapes count as non-loopback so a parse gap
 *   over-reports rather than silently hiding a reachable port.
 */
export function isLoopbackHexAddress(hex: string): boolean {
  const addr = hex.toUpperCase();
  if (addr.length === 8) {
    return addr.slice(6, 8) === "7F";
  }
  if (addr.length === 32) {
    // ::1
    if (addr === "00000000000000000000000001000000") return true;
    // v4-mapped: ::ffff:a.b.c.d — check the embedded v4 word for 127/8.
    if (addr.slice(0, 16) === "0000000000000000" && addr.slice(16, 24) === "FFFF0000") {
      return addr.slice(30, 32) === "7F";
    }
    return false;
  }
  return false;
}

/**
 * Parse /proc/net/tcp or /proc/net/tcp6 content into the LISTEN sockets with
 * their bind class. Format (fixed columns, header line first):
 *   sl  local_address rem_address   st ...
 *    0: 0100007F:0BB8 00000000:0000 0A ...
 * local_address is hex ip:port; st is the hex socket state.
 */
export function parseProcNetTcpListeners(content: string): ListeningSocket[] {
  const sockets: ListeningSocket[] = [];
  const lines = content.split("\n");
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (!line) continue;
    const cols = line.trim().split(/\s+/);
    // cols: [sl, local_address, rem_address, st, ...]
    if (cols.length < 4 || cols[3] !== PROC_TCP_LISTEN_STATE) continue;
    const local = cols[1];
    if (!local) continue;
    const [addrHex, portHex] = local.split(":");
    if (!addrHex || !portHex) continue;
    const port = Number.parseInt(portHex, 16);
    if (!Number.isInteger(port) || port < 1 || port > 65535) continue;
    sockets.push({ port, loopback: isLoopbackHexAddress(addrHex) });
  }
  return sockets;
}

/** Fold per-socket rows into the scan shape (loopback-only = no non-loopback
 *  listener exists for that port across every source scanned). */
export function collectScan(sockets: readonly ListeningSocket[]): ListeningPortScan {
  const ports = new Set<number>();
  const hasExternal = new Set<number>();
  for (const { port, loopback } of sockets) {
    ports.add(port);
    if (!loopback) hasExternal.add(port);
  }
  const loopbackOnly = new Set<number>();
  for (const port of ports) {
    if (!hasExternal.has(port)) loopbackOnly.add(port);
  }
  return { ports, loopbackOnly };
}

const DEFAULT_PROC_PATHS = ["/proc/net/tcp", "/proc/net/tcp6"];

/** Union of LISTEN sockets across the /proc sources. Null when none is
 *  readable (non-Linux host) so the poller can disable itself. */
export async function readProcListeningPorts(
  procPaths: readonly string[] = DEFAULT_PROC_PATHS,
): Promise<ListeningPortScan | null> {
  const sockets: ListeningSocket[] = [];
  let readable = false;
  for (const path of procPaths) {
    try {
      const content = await readFile(path, "utf8");
      readable = true;
      sockets.push(...parseProcNetTcpListeners(content));
    } catch {
      // Missing tcp6 (or non-Linux) — try the next source.
    }
  }
  return readable ? collectScan(sockets) : null;
}

/** darwin fallback for local dev/tests: parse `netstat -an -p tcp` LISTEN
 *  rows (local address column ends in `.<port>`). Never used on pods. */
async function readNetstatListeningPorts(): Promise<ListeningPortScan | null> {
  const output = await new Promise<string | null>((resolve) => {
    execFile("netstat", ["-an", "-p", "tcp"], { timeout: 5000 }, (err, stdout) => {
      resolve(err ? null : stdout);
    });
  });
  if (output === null) return null;
  const sockets: ListeningSocket[] = [];
  for (const line of output.split("\n")) {
    if (!line.includes("LISTEN")) continue;
    const cols = line.trim().split(/\s+/);
    const local = cols[3];
    if (!local) continue;
    const lastDot = local.lastIndexOf(".");
    if (lastDot < 0) continue;
    const host = local.slice(0, lastDot);
    const port = Number(local.slice(lastDot + 1));
    if (!Number.isInteger(port) || port < 1 || port > 65535) continue;
    const loopback = host.startsWith("127.") || host === "::1" || host === "localhost";
    sockets.push({ port, loopback });
  }
  return collectScan(sockets);
}

/** Platform-appropriate scan: /proc on Linux, netstat elsewhere (dev only). */
export async function readListeningPorts(): Promise<ListeningPortScan | null> {
  const proc = await readProcListeningPorts();
  if (proc !== null) return proc;
  if (process.platform !== "linux") return readNetstatListeningPorts();
  return null;
}

// ── Poller ──────────────────────────────────────────────────────────────────

/** Ports that are never preview candidates even when bound after baseline:
 *  pod sshd (2222) plus the DB/cache sidecars the API also deny-lists
 *  (authoritative filter is server-side; this is defense in depth). */
const DEFAULT_EXCLUDED_PORTS: readonly number[] = [2222, 5432, 6379, 9200];

/** Linux's default ip_local_port_range lower bound. LISTEN sockets up here
 *  are almost always transient auto-assigned ports (debug adapters, test
 *  runners binding :0), not dev servers a human wants previewed. */
const DEFAULT_EPHEMERAL_PORT_MIN = 32768;

const DEFAULT_INTERVAL_MS = 15_000;
const DEFAULT_MAX_PORTS = 16;
/** A port must survive this many consecutive scans to be reported, and be
 *  absent this many to be dropped — filters processes that bind briefly. */
const CONFIRM_SCANS = 2;

export interface PortDiscoveryOptions {
  /** Push the FULL current confirmed set (called only on change). */
  report: (ports: WorkspaceDiscoveredPort[]) => Promise<void>;
  intervalMs?: number;
  maxPorts?: number;
  excludedPorts?: readonly number[];
  /** Ports >= this are ignored as transient/ephemeral. Tests override. */
  ephemeralPortMin?: number;
  /** Scan override for tests; defaults to the platform scanner. */
  scan?: () => Promise<ListeningPortScan | null>;
  now?: () => Date;
  log?: (message: string) => void;
}

interface TrackedPort {
  /** Consecutive scans the port has been present (seen) or absent (missed). */
  seen: number;
  missed: number;
  confirmed: boolean;
  detectedAt: string;
}

export class PortDiscovery {
  private readonly opts: Required<Pick<PortDiscoveryOptions, "report">> & PortDiscoveryOptions;
  private readonly intervalMs: number;
  private readonly maxPorts: number;
  private readonly excluded: Set<number>;
  private readonly ephemeralPortMin: number;
  private readonly scan: () => Promise<ListeningPortScan | null>;
  private readonly now: () => Date;
  private readonly log: (message: string) => void;

  private baseline: Set<number> | null = null;
  private readonly tracked = new Map<number, TrackedPort>();
  /** Loopback-only candidates already warned about (once per port). */
  private readonly warnedLoopback = new Set<number>();
  private timer: ReturnType<typeof setInterval> | null = null;
  private ticking = false;
  private disabled = false;
  /** Set when the confirmed set changed (or a report failed) — cleared only
   *  after a successful report, so transient RPC failures retry next tick. */
  private reportPending = false;
  private lastReportedKey = "";

  constructor(options: PortDiscoveryOptions) {
    this.opts = options;
    this.intervalMs = options.intervalMs ?? DEFAULT_INTERVAL_MS;
    this.maxPorts = options.maxPorts ?? DEFAULT_MAX_PORTS;
    this.excluded = new Set(options.excludedPorts ?? DEFAULT_EXCLUDED_PORTS);
    this.ephemeralPortMin = options.ephemeralPortMin ?? DEFAULT_EPHEMERAL_PORT_MIN;
    this.scan = options.scan ?? readListeningPorts;
    this.now = options.now ?? (() => new Date());
    this.log = options.log ?? ((m) => process.stderr.write(`[conveyor-agent] ${m}\n`));
  }

  /** Take the baseline scan and start polling. Safe to call once. */
  async start(): Promise<void> {
    if (this.timer || this.disabled) return;
    const baseline = await this.scanSafe();
    if (baseline === null) {
      // No readable socket source on this host — permanently disable.
      this.disabled = true;
      this.log("Port discovery disabled: no listening-socket source available");
      return;
    }
    // Everything already bound (loopback or not) predates the start command
    // and is never a preview candidate.
    this.baseline = baseline.ports;
    this.timer = setInterval(() => void this.tick(), this.intervalMs);
    // Do not hold the process open for the poller.
    this.timer.unref?.();
  }

  stop(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  /** One poll cycle. Exposed for tests (deterministic, no timers needed). */
  async tick(): Promise<void> {
    if (this.ticking || this.disabled || !this.baseline) return;
    this.ticking = true;
    try {
      const current = await this.scanSafe();
      // Transient read failure — retry next tick.
      if (current === null) return;
      this.updateTracking(current);
      if (this.reportPending) await this.flushReport();
    } finally {
      this.ticking = false;
    }
  }

  private async scanSafe(): Promise<ListeningPortScan | null> {
    try {
      return await this.scan();
    } catch {
      return null;
    }
  }

  private isCandidate(port: number): boolean {
    if (this.baseline?.has(port)) return false;
    if (this.excluded.has(port)) return false;
    if (port >= this.ephemeralPortMin) return false;
    return true;
  }

  private updateTracking(current: ListeningPortScan): void {
    // Reachable = has at least one non-loopback listener. Loopback-only
    // candidates are unreachable via the pod IP — never track them, but
    // leave a breadcrumb so "why doesn't my preview work" is answerable.
    const reachable = new Set<number>();
    for (const port of current.ports) {
      if (!this.isCandidate(port)) continue;
      if (current.loopbackOnly.has(port)) {
        if (!this.warnedLoopback.has(port)) {
          this.warnedLoopback.add(port);
          this.log(
            `Port ${port} is listening on loopback only and cannot be previewed — ` +
              `bind 0.0.0.0 (or the pod IP) to make it reachable through the preview proxy`,
          );
        }
        continue;
      }
      reachable.add(port);
    }

    for (const port of reachable) {
      const entry = this.tracked.get(port);
      if (!entry) {
        this.tracked.set(port, { seen: 1, missed: 0, confirmed: false, detectedAt: "" });
        continue;
      }
      entry.seen += 1;
      entry.missed = 0;
      if (!entry.confirmed && entry.seen >= CONFIRM_SCANS) {
        entry.confirmed = true;
        entry.detectedAt = this.now().toISOString();
      }
    }
    for (const [port, entry] of this.tracked) {
      if (reachable.has(port)) continue;
      entry.missed += 1;
      entry.seen = 0;
      if (entry.missed >= CONFIRM_SCANS || !entry.confirmed) this.tracked.delete(port);
    }
    const key = this.confirmedKey();
    if (key !== this.lastReportedKey) this.reportPending = true;
  }

  private confirmedPorts(): WorkspaceDiscoveredPort[] {
    const confirmed = [...this.tracked.entries()]
      .filter(([, entry]) => entry.confirmed)
      .sort(([a], [b]) => a - b)
      .slice(0, this.maxPorts);
    return confirmed.map(([port, entry]) => ({
      port,
      protocol: "tcp" as const,
      detectedAt: entry.detectedAt,
    }));
  }

  private confirmedKey(): string {
    return this.confirmedPorts()
      .map(({ port }) => port)
      .join(",");
  }

  private async flushReport(): Promise<void> {
    const ports = this.confirmedPorts();
    const key = ports.map(({ port }) => port).join(",");
    try {
      await this.opts.report(ports);
      this.lastReportedKey = key;
      this.reportPending = false;
      this.log(`Discovered preview ports: [${key || "none"}]`);
    } catch {
      // Keep reportPending set — retried on the next tick.
    }
  }
}
